/**
 * Component registry — Tilleggsmoduler (C): components rendered inside shared
 * (B) code that belong to products NOT in the Dyrejournal build. Unlike the
 * card/drawer registries, the export stub here is a PLAIN EMPTY OBJECT
 * (`export default {};`) because the consumers have real fallback branches:
 * video sites gate their stream branch on `VideoStreamingPlayer &&` and fall
 * through to the existing plain mp4 <video> branch, list sites render nothing.
 *
 * VideoStreamingPlayer and AutoplayHlsVideo are the ONLY paths through which
 * hls.js reaches shared code — with this registry stubbed, the exported
 * Dyrejournal repo has zero hls.js references and drops the dependency.
 * Classification source of truth: docs/dyrejournal-separation/manifest.md
 */
import VideoStreamingPlayer from '../routes/videostreaming/player';
import AutoplayHlsVideo from '../components/streaming/autoplayHlsVideo';
import CartConventionAddons from '../components/cart/conventionAddons';
import UserRacesList from '../components/user/racesList';
import RubicStartCard from '../components/user/rubicStartCard';
import { articleTextColor, articleBackgroundStyles } from '../lib/articleBackground';
import * as backgroundPostText from '../lib/backgroundPostText';
import ConventionList from '../components/convention/list';
import CompetitionResultlList from '../components/competitionresult/list';
import ArticleList from '../components/article/list';
import InstallationList from '../components/installation/list';
import InspectionList from '../components/inspection/list';
import WorkSharingListCompact from '../components/workSharing/listCompact';
import ProfileImageFaceCheck from '../components/user/profileImageFaceCheck';
import ProfileFaceConsent from '../components/user/profileFaceConsent';
import ProfileMyPhotosTile from '../components/user/profileMyPhotosTile';
import ArcadeTrigger from '../components/arcade/trigger';
import AmmoMyPurchases from '../components/ammoSale/myPurchases';
import AmmoProfileTile from '../components/ammoSale/profileTile';
import WeaponCardList from '../components/weaponCard/list';
import MyClubKeys from '../components/clubOps/myKeys';
import ConventionCalendarToggle from '../components/convention/calendarToggle';
import ShotHistory, { ShotHistoryProfileTile } from '../components/user/shotHistory';
import ArticleSponsorOverlay from '../components/article/sponsorOverlay';
import RelatedArticles from '../components/article/relatedArticles';
import EventsWidget from '../components/article/eventsWidget';
import ElearningBadges from '../components/elearning/badges';

// IMPORT-CYCLE CONSTRAINT: the C components imported above import shared B
// components (gui lists, etc.) that import this registry back. Therefore:
// (a) the exports are GETTERS reading the live bindings at access time, and
// (b) consumers must access members at RENDER time (componentsOther.X inside
//     render), never destructure at module scope.
export default {
    get VideoStreamingPlayer() { return VideoStreamingPlayer; },
    get AutoplayHlsVideo() { return AutoplayHlsVideo; },
    get CartConventionAddons() { return CartConventionAddons; },
    get UserRacesList() { return UserRacesList; },
    get RubicStartCard() { return RubicStartCard; },
    // Not a component: C helper referenced from shared code (gui/listFrontpage).
    get articleTextColor() { return articleTextColor; },
    // Not a component: C helper referenced from shared code (gui/listFrontpage).
    get articleBackgroundStyles() { return articleBackgroundStyles; },
    // Not a component: C helper module (per-line text sizing for background
    // posts) referenced from shared code (gui/listFrontpage).
    get backgroundPostText() { return backgroundPostText; },
    get ConventionList() { return ConventionList; },
    get CompetitionResultlList() { return CompetitionResultlList; },
    get ArticleList() { return ArticleList; },
    get InstallationList() { return InstallationList; },
    get InspectionList() { return InspectionList; },
    get WorkSharingListCompact() { return WorkSharingListCompact; },
    // Calendar toolbar toggle that shows conventions (stevner/treninger) as
    // events in the shared logged-in calendar. Absent in the Dyrejournal build,
    // so components/calendar/new.js neither renders it nor asks the backend
    // for conventions (includeConventions=1).
    get ConventionCalendarToggle() { return ConventionCalendarToggle; },
    // Stage 0 profile face detection. The POST target (/api/faces/detect) is a C
    // mount (mounts-other.js), so in the Dyrejournal build the endpoint is gone;
    // rendering it there would 404 on every profile-edit mount, permanently,
    // because needsDetection never becomes false without a stored result.
    // routes/users/profile.js therefore renders it through this registry.
    get ProfileImageFaceCheck() { return ProfileImageFaceCheck; },
    // B1 face enrolment/consent. The endpoints (/api/faces/*) are C mounts, so
    // in the Dyrejournal build they are gone; rendering this there would show a
    // consent panel whose every action 404s.
    get ProfileFaceConsent() { return ProfileFaceConsent; },
    // B2 "Photos of me" profile tile. /my-photos is a C route, so in the
    // Dyrejournal build this getter is gone and profile.js's guard drops the
    // tile rather than routing to a page that does not exist.
    get ProfileMyPhotosTile() { return ProfileMyPhotosTile; },
    // «Sertifiseringer» block on the profile: e-learning badges (C). The
    // /api/elearning* mounts are C, so the Dyrejournal build omits the block.
    get ProfileElearningBadges() { return ElearningBadges; },
    // Hidden arcade easter egg trigger (Konami code / logo taps). Mounted once
    // from app.js; absent in the Dyrejournal build, so the app shell guards it.
    get ArcadeTrigger() { return ArcadeTrigger; },
    get AmmoMyPurchases() { return AmmoMyPurchases; },
    get AmmoProfileTile() { return AmmoProfileTile; },
    // Profile «Skudd» tab: the shooter's own Megalink shot history (C, shooting).
    get ShotHistory() { return ShotHistory; },
    get ShotHistoryProfileTile() { return ShotHistoryProfileTile; },
    get WeaponCardList() { return WeaponCardList; },
    get MyClubKeys() { return MyClubKeys; },
    // Article sponsor logos over the frontpage hero (gui/listFrontpage). Absent
    // in the Dyrejournal build, so the hero renders without the sponsor bar.
    get ArticleSponsorOverlay() { return ArticleSponsorOverlay; },
    // «Related articles» block under a help-center article (help/index). Absent
    // in the Dyrejournal build, so the help article ends after prev/next.
    get RelatedArticles() { return RelatedArticles; },
    // {{events}} marker widget in an article body (articleRender/bodyRenderer).
    // Absent in the Dyrejournal build, so the marker renders nothing.
    get EventsWidget() { return EventsWidget; },
};
